import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import Loading from './Loading';

// Check if running in Electron
const isElectron = Boolean(window.electronAPI);

const OAuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const { user, isLoading } = useAuth();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [message, setMessage] = useState('Giriş işleniyor...');
    const [deepLinkUrl, setDeepLinkUrl] = useState<string | null>(null);
    const [showDeepLink, setShowDeepLink] = useState(false);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Get the hash from URL (contains access_token, etc.)
                const hash = window.location.hash;

                if (hash) {
                    // Parse the hash to get tokens
                    const params = new URLSearchParams(hash.substring(1));
                    const accessToken = params.get('access_token');
                    const refreshToken = params.get('refresh_token');
                    const errorParam = params.get('error');
                    const errorDescription = params.get('error_description');

                    if (errorParam) {
                        throw new Error(errorDescription || 'OAuth authentication failed');
                    }

                    if (accessToken && refreshToken) {
                        // Check URL for electron param
                        const urlParams = new URLSearchParams(window.location.search);
                        const fromElectron = urlParams.get('electron') === 'true';

                        if (fromElectron || !isElectron) {
                            // If we're in a browser (not Electron) and user came from Electron OR
                            // we're checking if this is from Electron app
                            if (fromElectron) {
                                // Generate and auto-open deep link for Electron
                                const electronDeepLink = `servers://auth/callback?access_token=${encodeURIComponent(accessToken)}&refresh_token=${encodeURIComponent(refreshToken)}`;
                                setDeepLinkUrl(electronDeepLink);
                                setShowDeepLink(true);
                                setStatus('success');
                                setMessage('Giriş başarılı! Electron uygulamasına dönülüyor...');

                                // Auto-open the deep link
                                setTimeout(() => {
                                    window.location.href = electronDeepLink;
                                }, 500);
                                return;
                            }
                        }

                        // Regular web flow OR already in Electron
                        const { error } = await supabase.auth.setSession({
                            access_token: accessToken,
                            refresh_token: refreshToken,
                        });

                        if (error) throw error;

                        setStatus('success');
                        setMessage('Giriş başarılı! Yönlendiriliyorsunuz...');
                        setTimeout(() => {
                            navigate('/');
                        }, 1500);
                    } else {
                        // No tokens in URL, might already be logged in
                        setStatus('success');
                        setMessage('Yönlendiriliyorsunuz...');
                        setTimeout(() => {
                            navigate('/');
                        }, 1000);
                    }
                } else {
                    // No hash, check if already logged in
                    const { data: { session } } = await supabase.auth.getSession();
                    if (session) {
                        navigate('/');
                    } else {
                        setStatus('error');
                        setMessage('OAuth bilgisi bulunamadı.');
                    }
                }
            } catch (error) {
                console.error('OAuth callback error:', error);
                setStatus('error');
                setMessage(error instanceof Error ? error.message : 'Giriş sırasında bir hata oluştu.');
            }
        };

        handleCallback();
    }, [navigate]);

    // If already logged in via AuthContext, redirect
    useEffect(() => {
        if (!isLoading && user && status === 'processing') {
            if (user.needsCompletion) {
                navigate('/complete-registration');
            } else {
                navigate('/');
            }
        }
    }, [user, isLoading, navigate, status]);

    const handleReturnToElectron = () => {
        if (deepLinkUrl) {
            window.location.href = deepLinkUrl;
        }
    };

    if (isLoading) {
        return <Loading message="Oturum kontrol ediliyor..." />;
    }

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
            <div className="text-center p-8 bg-gray-800 rounded-2xl shadow-xl max-w-md mx-4">
                {status === 'processing' && (
                    <>
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-300">{message}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">Giriş Başarılı!</h2>
                        <p className="text-gray-300 mb-4">{message}</p>

                        {/* Deep link button for Electron */}
                        {deepLinkUrl && (
                            <div className="mt-4 space-y-3">
                                <button
                                    onClick={handleReturnToElectron}
                                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-[1.02]"
                                >
                                    🚀 Electron Uygulamasına Dön
                                </button>
                                <p className="text-gray-500 text-xs">
                                    Tıkladığınızda uygulama otomatik olarak açılacak.
                                </p>
                            </div>
                        )}
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">Hata</h2>
                        <p className="text-gray-300 mb-4">{message}</p>
                        <button
                            onClick={() => navigate('/login')}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                        >
                            Tekrar Dene
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default OAuthCallback;
